import type { ChipIntent } from '@/components/common/chip';
import { formatInAppTz } from '@/lib/date';

import type { ReplacementReason, ReplacementStatus } from './types';

/** Order Date & Time: the UTC wire instant on the application (Eastern) clock (matches Orders). */
export function formatReplacementDateTime(iso: string): string {
  return formatInAppTz(iso, 'D MMM YYYY | hh:mm A');
}

/** Replacement Request Date: the Eastern day of the request instant (no time — the column is a
 *  "Date", not "Date & Time"), which is also the day the Request Date filter matches it on. */
export function formatReplacementDate(iso: string): string {
  return formatInAppTz(iso, 'D MMM YYYY');
}

/** Status chip label + Figma colorway (leaf nodes 408:276490/276496/276498):
 *  Pending = yellow (`review`), Approved = green (`approved`), Rejected = red (`attention`). */
export const REPLACEMENT_STATUS_CHIP: Record<
  ReplacementStatus,
  { label: string; intent: ChipIntent }
> = {
  pending: { label: 'Pending', intent: 'review' },
  approved: { label: 'Approved', intent: 'approved' },
  rejected: { label: 'Rejected', intent: 'attention' },
};

/** The six webstore reasons (US 112366) → display labels (CONTEXT.md). */
export const REPLACEMENT_REASONS: Record<ReplacementReason, string> = {
  item_arrived_damaged: 'Item arrived damaged',
  missing_parts_or_accessories: 'Missing parts or accessories',
  incorrect_item_delivered: 'Incorrect item delivered',
  defective_or_malfunctioning: 'Defective or malfunctioning',
  poor_quality_not_as_pictured: 'Poor quality / Not as pictured',
  others: 'Others',
};

/** Activity-timeline event label → chip colorway (Figma 429:293436 and 429:311617): Created=blue,
 *  Pending=yellow, Approved=green, Rejected=red; anything else neutral. Shared by the queue's
 *  detail drawer and the order-scoped Ticket Preview, whose timelines render identical chips. */
const TIMELINE_CHIP: Record<string, ChipIntent> = {
  created: 'published',
  pending: 'review',
  approved: 'approved',
  rejected: 'attention',
};

export const timelineChipIntent = (label: string): ChipIntent =>
  TIMELINE_CHIP[label.trim().toLowerCase()] ?? 'neutral';
